import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Megaphone, Sparkles, Gift, PawPrint, CalendarHeart, ShoppingBag, Heart, Star, Bell, ArrowRight } from "lucide-react";
import api from "@/lib/api";

export const POPUP_ICONS = {
  megaphone: Megaphone,
  sparkles: Sparkles,
  gift: Gift,
  paw: PawPrint,
  calendar: CalendarHeart,
  bag: ShoppingBag,
  heart: Heart,
  star: Star,
  bell: Bell,
};

export const POPUP_SECTION_OPTIONS = ["about", "services", "nagayu", "selfwash", "grooming", "gallery", "contact", "shop"];

// Site-wide promo popup (dev-managed). Shows once per session, never on admin pages.
export const SitePopup = () => {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    api.get("/popup").then((r) => {
      const data = r.data;
      if (!data.enabled) return;
      const sig = JSON.stringify([data.title, data.text, data.btn_label, data.icon]);
      if (sessionStorage.getItem("cfm_popup_seen") === sig) return;
      sessionStorage.setItem("cfm_popup_seen", sig);
      setCfg(data);
      const t = setTimeout(() => setOpen(true), 1200);
      return () => clearTimeout(t);
    }).catch(() => {});
  }, [location.pathname]);

  const go = () => {
    setOpen(false);
    if (cfg.btn_kind === "section") {
      if (cfg.btn_section === "shop") {
        navigate("/shop");
        return;
      }
      if (location.pathname !== "/") {
        navigate(`/#${cfg.btn_section}`);
        return;
      }
      document.getElementById(cfg.btn_section)?.scrollIntoView({ behavior: "smooth" });
    } else if (cfg.btn_kind === "url" && cfg.btn_url) {
      window.open(cfg.btn_url, "_blank", "noopener,noreferrer");
    }
  };

  const Icon = POPUP_ICONS[cfg?.icon] || Sparkles;

  return (
    <AnimatePresence>
      {open && cfg && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] grid place-items-center bg-brand-ink/40 backdrop-blur-sm px-5"
          onClick={() => setOpen(false)}
          data-testid="site-popup-overlay"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-w-md w-full bg-white rounded-[2rem] border border-brand/10 shadow-[0_30px_80px_rgba(42,37,34,0.25)] p-8 sm:p-10 text-center overflow-hidden"
            data-testid="site-popup"
            role="dialog"
            aria-modal="true"
          >
            <PawPrint className="absolute -right-8 -bottom-8 h-40 w-40 text-brand/[0.05] rotate-12 pointer-events-none" />
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 grid place-items-center h-9 w-9 rounded-full text-brand-ink/40 hover:bg-brand-soft transition-colors no-a11y-underline"
              data-testid="site-popup-close"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
            <span className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-brand-soft text-brand mb-5">
              <Icon className="h-7 w-7" />
            </span>
            {cfg.title && <h2 className="font-heading text-3xl font-bold text-brand-ink" data-testid="site-popup-title">{cfg.title}</h2>}
            {cfg.text && <p className="mt-3 text-brand-muted leading-relaxed whitespace-pre-line" data-testid="site-popup-text">{cfg.text}</p>}
            {cfg.btn_kind !== "none" && cfg.btn_label && (
              <button
                onClick={go}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold px-7 h-12 transition-colors no-a11y-underline"
                data-testid="site-popup-cta"
              >
                {cfg.btn_label} <ArrowRight className="h-4 w-4" />
              </button>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SitePopup;
